import { useRef } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { categoriesData } from "../../../../helpers/Arrays";
import { getCompetitionCategoryInfo } from "../../../../helpers/competitionMeta";
import CompetitionCard from "../../../molecules/Cards/CompetitionCard/CompetitionCard";
import "swiper/css";
import "swiper/css/pagination";
import "./CategoriesCarousel.css";

const CategoriesCarousel = () => {
  const navigate = useNavigate();
  const swiperRef = useRef(null);

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(180deg, #fafbfc 0%, #ffffff 45%, #f7f9fc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(700px 400px at 10% 15%, rgba(35, 174, 244, 0.10), transparent 60%), radial-gradient(600px 380px at 90% 80%, rgba(193, 26, 131, 0.08), transparent 55%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "1500px",
          margin: "0 auto",
          padding: { xs: "30px 20px", sm: "30px 60px", lg: "50px 120px" },
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: "720px", margin: "0 auto" }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.75,
              py: 0.75,
              mb: 2,
              borderRadius: "999px",
              backgroundColor: "rgba(35, 174, 244, 0.10)",
              border: "1px solid rgba(35, 174, 244, 0.22)",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "12px", md: "13px" },
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#23aef4",
              }}
            >
              Competition Categories
            </Typography>
          </Box>

          <Typography
            component="h2"
            sx={{
              color: "primary.darker",
              fontSize: { xs: "28px", sm: "34px", md: "42px" },
              lineHeight: 1.15,
              fontWeight: 800,
              letterSpacing: "-0.02em",
            }}
          >
            Find Your{" "}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Challenge
            </Box>
          </Typography>

          <Box
            sx={{
              height: "4px",
              width: { xs: "80px", md: "220px" },
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, #23aef4 0%, #c11a83 100%)",
              margin: "16px auto 20px",
            }}
          />

          <Typography
            sx={{
              color: "primary.darker",
              lineHeight: 1.4,
              fontSize: { xs: "15px", md: "16px" },
              opacity: 0.85,
              mb: 3,
            }}
          >
            Explore robotics competitions tailored for different age groups and
            skill levels — from virtual challenges to hands-on robot missions.
          </Typography>

          <Button
            variant="outlined"
            onClick={() => navigate("/categories")}
            endIcon={<ArrowForwardIcon />}
            sx={{
              mb: { xs: 4, md: 5 },
              px: 2.5,
              py: 1.1,
              borderRadius: "999px",
              fontSize: "14px",
              fontWeight: 700,
              textTransform: "none",
              color: "#23aef4",
              borderColor: "rgba(35, 174, 244, 0.45)",
              backgroundColor: "#fff",
              boxShadow: "0 8px 24px rgba(10, 14, 24, 0.06)",
              "&:hover": {
                borderColor: "#23aef4",
                backgroundColor: "rgba(35, 174, 244, 0.06)",
              },
            }}
          >
            View all categories
          </Button>
        </Box>

        <Box sx={{ position: "relative" }}>
          <Swiper
            className="categories-swiper"
            modules={[Pagination, Autoplay]}
            spaceBetween={24}
            slidesPerView={1.15}
            centeredSlides
            loop
            loopAddBlankSlides
            loopAdditionalSlides={3}
            watchOverflow={false}
            slidesPerGroup={1}
            speed={600}
            autoplay={{
              delay: 4500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{ clickable: true }}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            breakpoints={{
              600: {
                slidesPerView: 1.6,
                spaceBetween: 20,
                loopAdditionalSlides: 3,
              },
              900: {
                slidesPerView: 2.2,
                spaceBetween: 24,
                loopAdditionalSlides: 3,
              },
              1200: {
                slidesPerView: 3,
                spaceBetween: 28,
                loopAdditionalSlides: 3,
              },
            }}
          >
            {categoriesData.map((item, i) => {
              const categoryInfo = getCompetitionCategoryInfo(item.type);

              return (
                <SwiperSlide key={`${item.link}-${i}`}>
                  <CompetitionCard
                    item={item}
                    index={i}
                    categoryTitle={categoryInfo.title}
                    accent={categoryInfo.accent}
                    onClick={() => navigate(item.link)}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              gap: 1.5,
              mt: 3,
              position: "relative",
              zIndex: 5,
            }}
          >
            <IconButton
              type="button"
              aria-label="Previous category"
              onClick={() => swiperRef.current?.slidePrev()}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: "#fff",
                border: "1px solid rgba(89, 92, 97, 0.14)",
                boxShadow: "0 8px 24px rgba(10, 14, 24, 0.08)",
                color: "primary.darker",
                "&:hover": {
                  backgroundColor: "rgba(35, 174, 244, 0.08)",
                  borderColor: "rgba(35, 174, 244, 0.35)",
                  color: "#23aef4",
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              type="button"
              aria-label="Next category"
              onClick={() => swiperRef.current?.slideNext()}
              sx={{
                width: 48,
                height: 48,
                backgroundColor: "#fff",
                border: "1px solid rgba(89, 92, 97, 0.14)",
                boxShadow: "0 8px 24px rgba(10, 14, 24, 0.08)",
                color: "primary.darker",
                "&:hover": {
                  backgroundColor: "rgba(35, 174, 244, 0.08)",
                  borderColor: "rgba(35, 174, 244, 0.35)",
                  color: "#23aef4",
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CategoriesCarousel;
